/**
 * Single source of truth for which origin serves the authenticated API for this tab.
 *
 * The document may stay on a geo-proximate host (e.g. dev.hyperfabric.cisco.com) while
 * REST is 307-redirected to a regional host (eu.dev / us.dev). Fetch follows redirects, but
 * the browser still uses the page origin for relative URLs -- so we record the final
 * response URL origin after redirects and use it for:
 *   - All subsequent ReST.fetch URLs (see `getRestUrl`)
 *   - Sync-follow WebSocket hosts (wss)
 *
 * When the active origin matches the page origin, the override is cleared and behavior
 * matches plain relative URLs + location.host.
 *
 * WebSocket failures do not carry HTTP redirects; if we need to recover from a stale origin,
 * a successful REST call (e.g. session refresh) will re-record the correct origin from
 * `response.url`.
 */

function isTrustedApiOriginHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return (
    h.endsWith(".hyperfabric.cisco.com") ||
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "::1"
  );
}

/**
 * Updates the active API origin from a completed fetch (uses final URL after redirects).
 */
export function updateActiveApiOriginFromFetchResponse(
  response: Response
): void {
  try {
    if (!window.cnc) {
      return;
    }
    const resolved = new URL(response.url);
    if (!isTrustedApiOriginHostname(resolved.hostname)) {
      return;
    }
    if (resolved.origin !== window.location.origin) {
      window.cnc.activeApiOrigin = resolved.origin;
    } else {
      window.cnc.activeApiOrigin = undefined;
    }
  } catch {
    /* response.url was not a valid URL -- leave activeApiOrigin unchanged */
  }
}

/** Clears the active API origin override (logout, tenant switch, 401). */
export function clearActiveApiOrigin(): void {
  if (typeof window !== "undefined" && window.cnc) {
    window.cnc.activeApiOrigin = undefined;
  }
}

/** Origin for API calls; defaults to the document origin. */
export function getActiveApiOrigin(): string {
  return window.cnc?.activeApiOrigin ?? window.location.origin;
}

/**
 * Returns the URL to use for a REST path. Relative paths are joined to the active API
 * origin (see `getActiveApiOrigin()`); absolute URLs are returned unchanged.
 */
export function getRestUrl(url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  const origin = getActiveApiOrigin();
  if (url.startsWith("/")) {
    return `${origin}${url}`;
  }
  return `${origin}/${url}`;
}

/** Host for wss:// WebSocket URLs. */
export function getWebSocketHost(): string {
  try {
    return new URL(getActiveApiOrigin()).host;
  } catch {
    return location.host;
  }
}
